import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BdvAsyncManagementService } from '../../services/bdv-async-management.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-bdv-async-management',
  templateUrl: './bdv-async-management.component.html',
  styleUrls: ['./bdv-async-management.component.css']
})
export class BdvAsyncManagementComponent implements OnInit {

  asyncStatus: any = null;
  allConfigs: any[] = [];
  loading = false;
  error: string | null = null;

  constructor(
    private bdvAsyncService: BdvAsyncManagementService,
    private router: Router
  ) {}

  ngOnInit(): void {
    console.log('🚀 Componente BDV Async Management iniciado');
    this.loadAsyncStatus();
  }

  // ========================================
  // MÉTODOS PRINCIPALES
  // ========================================

  public loadAsyncStatus(): void {
    this.loading = true;
    this.error = null;
    
    this.bdvAsyncService.getAsyncStatus().subscribe({
      next: (response: any) => {
        this.asyncStatus = response.data;
        this.loading = false;
        console.log('✅ Estado async cargado:', this.asyncStatus);
      },
      error: (error: any) => {
        this.loading = false;
        this.error = 'Error conectando con BDV API';
        console.error('❌ Error cargando estado:', error);
      }
    });
  }

  public toggleAsyncVerification(): void {
    if (!this.asyncStatus) return;
    
    const newStatus = !this.asyncStatus.async_verification_enabled;
    const action = newStatus ? 'habilitar' : 'deshabilitar';
    
    Swal.fire({
      title: `¿${action.charAt(0).toUpperCase() + action.slice(1)} Verificación Asíncrona?`,
      text: `Esto ${action}á el encolamiento automático de transacciones BDV`,
      icon: newStatus ? 'question' : 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#28a745' : '#dc3545',
      confirmButtonText: `Sí, ${action}`,
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        
        this.bdvAsyncService.toggleAsync(newStatus).subscribe({
          next: (response: any) => {
            this.asyncStatus = response.data;
            this.loading = false;
            
            Swal.fire({
              title: '¡Configuración Actualizada!',
              text: `Verificación asíncrona ${action}da exitosamente`,
              icon: 'success',
              timer: 3000,
              showConfirmButton: false
            });
          },
          error: (error: any) => {
            this.loading = false;
            console.error('❌ Error en toggle:', error);
            
            Swal.fire({
              title: 'Error',
              text: 'No se pudo actualizar la configuración. Verifica la conexión con BDV API.',
              icon: 'error'
            });
          }
        });
      }
    });
  }

  public goBack(): void {
    this.router.navigate(['/console/payment-options']);
  }

  // ========================================
  // HELPERS
  // ========================================

  public getStatusColor(status: string): string {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'DISABLED': return 'danger';
      default: return 'secondary';
    }
  }

  public formatNumber(value: number): string {
    return value ? value.toLocaleString() : '0';
  }

  // ========================================
  // CONTROLES DE CONFIGURACIÓN
  // ========================================

  public onRetryAttemptsInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target && target.value) {
      this.changeRetryAttempts(target.value);
    }
  }

  public onRetryDelayInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target && target.value) {
      this.changeRetryDelay(target.value);
    }
  }

  public changeRetryAttempts(newValue: any): void {
    const value = parseInt(newValue);
    
    if (isNaN(value) || value < 1 || value > 50) {
      Swal.fire({
        title: 'Valor Inválido',
        text: 'El número de reintentos debe estar entre 1 y 50',
        icon: 'error'
      });
      return;
    }

    if (value === this.asyncStatus.max_retry_attempts) return;

    Swal.fire({
      title: '¿Cambiar Reintentos Máximos?',
      text: `Cambiar de ${this.asyncStatus.max_retry_attempts} a ${value} reintentos`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.updateConfigInBackend('max_async_retry_attempts', value);
      }
    });
  }

  public changeRetryDelay(newValue: any): void {
    const value = parseInt(newValue);
    
    if (isNaN(value) || value < 1 || value > 60) {
      Swal.fire({
        title: 'Valor Inválido',
        text: 'El delay debe estar entre 1 y 60 minutos',
        icon: 'error'
      });
      return;
    }

    if (value === this.asyncStatus.retry_delay_minutes) return;

    Swal.fire({
      title: '¿Cambiar Delay entre Reintentos?',
      text: `Cambiar de ${this.asyncStatus.retry_delay_minutes} a ${value} minutos`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.updateConfigInBackend('async_retry_delay_minutes', value);
      }
    });
  }

  private updateConfigInBackend(configKey: string, value: any): void {
    this.loading = true;
    
    this.bdvAsyncService.updateConfig(configKey, value).subscribe({
      next: (response: any) => {
        this.loading = false;
        // Recargar estado para mostrar cambios
        this.loadAsyncStatus();
        
        Swal.fire({
          title: '¡Configuración Actualizada!',
          text: `${configKey} actualizado exitosamente`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      },
      error: (error: any) => {
        this.loading = false;
        console.error(`❌ Error actualizando ${configKey}:`, error);
        
        Swal.fire({
          title: 'Error',
          text: `No se pudo actualizar ${configKey}`,
          icon: 'error'
        });
      }
    });
  }
}
